import React, { useEffect, useRef, useState } from 'react'
import Layout from '../common/Layout'
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from 'react-hook-form';
import ApiHelper from '../../helperFunction/ApiHelper';
import API_ENDPOINTS from '../../helperFunction/Constant';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabPanel, TabView } from 'primereact/tabview';
import AddContent from './AddContent';
import AddRelatedCourses from './AddRelatedCourses';
import AddCrossSell from './AddCrossSell';

const CourseAddEdit = () => {

    const { id } = useParams()
    const isEdit = id ? true : false
    const toast = useRef(null)
    const navigate = useNavigate()

    const [courseData, setCourseData] = useState([])
    const [categoryData, setCategoryData] = useState([])
    const [languages, setLanguages] = useState([])

    const numProps = (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()

    const schema = yup.object({
        categoryId: yup.string().required("Category is required"),
        languageId: yup.string().required("Language is required"),
        courseName: yup.string().required("Course name is required"),
        courseDescription: yup.string().required("Description is required"),
        features: yup.string().notRequired(),
        whatYouWillLearn: yup.string().notRequired(),
        courseMrp: yup.number().transform((value, originalValue) => (originalValue === "" || isNaN(originalValue)) ? null : value).nullable().required("MRP is required").min(0, "MRP cannot be negative"),
        coursePrice: yup.number().transform((value, originalValue) => (originalValue === "" || isNaN(originalValue)) ? null : value).nullable().required("Price is required").min(0, "Price cannot be negative"),
        courseRating: yup.number().transform((value, originalValue) => (originalValue === "" || isNaN(originalValue)) ? null : value).nullable().required("Rating is required").max(5, "Value Must be less than or equal to 5"),
        courseNumberOfRating: yup.number().transform((value, originalValue) => (originalValue === "" || isNaN(originalValue)) ? null : value).nullable().required("Number Of Rating is Required"),
        isActive: yup.boolean(),
        courseImage: yup.mixed().test("image-required", "Course thumbnail is required", function (value) { return ((value && value.length > 0) || courseData?.courseImage); }),
        searchKeywords: yup.string().optional().nullable(),
        courseTag: yup.string().optional().nullable(),
        trailerUrl: yup.string().required("Trailer Url is required"),
        studentsEnrolled: yup
            .number()
            .transform((value, originalValue) =>
                originalValue === "" || isNaN(originalValue) ? null : value
            )
            .nullable()
            .integer("Students Enrolled must be a whole number")
            .min(1, "Students Enrolled must be greater than 0")
            .notRequired(),
        courseLevel: yup.string().required("Course Level is required")
    });

    const { register, handleSubmit, reset, resetField, watch, formState: { errors }, } = useForm({ resolver: yupResolver(schema), });

    const resetValue = () => {
        reset({
            categoryId: '',
            languageId: '',
            courseName: '',
            courseDescription: '',
            features: '',
            whatYouWillLearn: '',
            courseMrp: '',
            coursePrice: '',
            courseRating: '',
            courseNumberOfRating: '',
            isActive: '',
            courseImage: null,
            searchKeywords: '',
            courseTag: '',
            trailerUrl: '',
            studentsEnrolled: '',
            courseLevel: '',
        })
    }

    const previewImage = watch('courseImage')

    useEffect(() => {
        const loadData = async () => {
            await Promise.all([
                getCategoryData(),
                getLanguages()
            ]);
            
            if (isEdit) {
                getCourseData();
            }
        };
        loadData();
    }, []);


    const getCategoryData = async () => {
        await ApiHelper.get(`${API_ENDPOINTS.CATEGORY}?active=true`).then((res) => {
            if (res.status === 200) {
                setCategoryData(res.data.data)
            }
        }).catch((error) => {
            console.log("Error: ", error);
        })
    }

    const getLanguages = async () => {
        await ApiHelper.get(`${API_ENDPOINTS.LANGUAGES}`).then((res) => {
            if (res.status === 200) {
                setLanguages(res.data.data)
            }
        }).catch((error) => {
            console.log("Error: ", error);
        })
    }

    const getCourseData = () => {
        ApiHelper.get(`${API_ENDPOINTS.COURSES}/${id}`).then((res) => {
            if (res.status === 200) {
                setCourseData(res.data.data)
                reset({
                    categoryId: res.data.data.categoryId,
                    languageId: res.data.data.languageId,
                    courseName: res.data.data.courseName,
                    courseDescription: res.data.data.courseDescription,
                    features: res.data.data.features,
                    whatYouWillLearn: res.data.data.whatYouWillLearn,
                    courseMrp: res.data.data.courseMrp,
                    coursePrice: res.data.data.coursePrice,
                    courseRating: res.data.data.courseRating,
                    courseNumberOfRating: res.data.data.courseNumberOfRating,
                    isActive: res.data.data.isActive,
                    searchKeywords: res.data.data.searchKeywords,
                    courseTag: res.data.data.courseTag,
                    trailerUrl: res.data.data.trailerUrl,
                    studentsEnrolled: res.data.data.studentsEnrolled,
                    courseLevel: res.data.data.courseLevel
                })
            }
        }).catch((error) => {
            console.log("Error: ", error);
        })
    }

    const onSubmit = (data) => {
        const formData = new FormData()
        for (const [key, value] of Object.entries(data)) {
            formData.append(key, value);
        };
        if (data.courseImage) {
            formData.append('courseImage', data.courseImage[0]);
        }
        if (isEdit) {
            update(formData)
        } else {
            add(formData)
        }
    };

    const add = (data) => {
        ApiHelper.post(`${API_ENDPOINTS.COURSES}/`, data).then((res) => {
            if (res.status === 200) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: res.data.message, life: 3000 })
                setTimeout(() => {
                    navigate(`/courses/edit/${res.data?.courseId}`)
                }, 900)
            }
        }).catch((error) => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.response.data.message, life: 3000 })
        })
    };

    const update = (data) => {
        ApiHelper.put(`${API_ENDPOINTS.COURSES}/${id}`, data).then((res) => {
            if (res.status === 200) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: res.data.message, life: 3000 })
                setTimeout(() => {
                    navigate('/courses')
                }, 900)
            }
        }).catch((error) => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.response.data.message, life: 3000 })
        })
    };

    const confirmDeleteDailog = () => {
        confirmDialog({
            message: 'Do you want to delete this record?',
            header: 'Delete Confirmation',
            icon: 'pi pi-info-circle',
            defaultFocus: null,
            acceptClassName: 'p-button-danger',
            accept: () => deleteImage(),
        });
    };

    const deleteImage = () => {
        ApiHelper.post(`${API_ENDPOINTS.COURSES}/delete-image/${id}`).then((res) => {
            if (res.status === 200) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: res.data.message, life: 3000 });
                getCourseData();
            }
        }).catch((err) => {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message, life: 3000 });
        });
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <Layout>
                <div class="content-wrapper">
                    <div class="container-xxl flex-grow-1 container-p-y">
                        <div class="d-flex justify-content-between align-items-center py-3">
                            <h4 class="fw-bold py-3 mb-2">
                                <span class="d-flex text-muted fw-light">
                                    <p class="breadcrumb-item pageHeader active me-2 mb-0" aria-current="page" onClick={() => navigate('/courses')}> Courses </p> / <b className='mx-2'> {isEdit ? 'Edit' : "Add"} </b>
                                </span>
                            </h4>
                            <Link to="/courses" class="btn btn-secondary">⬅ Go Back </Link>
                        </div>
                        <div class="row">
                            <div class="col-md-12">
                                <div class="card mb-4">
                                    <div class="card-body">
                                        <TabView>
                                            <TabPanel header="Basic Information">
                                                <form id="productForm" onSubmit={handleSubmit(onSubmit)}>
                                                    <div class="row mb-3">
                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Name</label>
                                                            <input type="text" name="name" class="form-control" placeholder='Name' {...register("courseName")} />
                                                            <small className="text-danger">{errors.courseName?.message}</small>
                                                        </div>
                                                        <div class="col-md-9 mb-3">
                                                            <label class="form-label">Description</label>
                                                            <textarea rows={4} type="text" name="description" class="form-control" placeholder='Description' {...register("courseDescription")} />
                                                            <small className="text-danger">{errors.courseDescription?.message}</small>
                                                        </div>
                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">MRP</label>
                                                            <input type="number" onKeyDown={numProps} name="mrp" min="0" class="form-control" {...register("courseMrp", { valueAsNumber: true, })} />
                                                            <small className="text-danger">{errors.courseMrp?.message}</small>
                                                        </div>
                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Price</label>
                                                            <input type="number" onKeyDown={numProps} name="price" class="form-control" {...register("coursePrice", { valueAsNumber: true, })} />
                                                            <small className="text-danger">{errors.coursePrice?.message}</small>
                                                        </div>
                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Rating</label>
                                                            <input type="number" onKeyDown={numProps} step="0.1" name="courseRating" class="form-control" {...register("courseRating", { valueAsNumber: true, })} />
                                                            <small className="text-danger">{errors.courseRating?.message}</small>
                                                        </div>
                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Number of Rating</label>
                                                            <input type="number" onKeyDown={numProps} name="courseNumberOfRating" class="form-control" {...register("courseNumberOfRating")} />
                                                            <small className="text-danger">{errors.courseNumberOfRating?.message}</small>
                                                        </div>
                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Category</label>
                                                            <select id="mySelect" name="categoryId" class="form-select" {...register("categoryId")} >
                                                                <option value="">Select Category</option>
                                                                {categoryData.map((item) => (
                                                                    <option key={item.categoryId} value={item.categoryId}>{item.categoryName}</option>
                                                                ))}
                                                            </select>
                                                            <small className="text-danger"> {errors.categoryId?.message} </small>
                                                        </div>

                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Language</label>
                                                            <select id="mySelect" name="languageId" class="form-select" {...register("languageId")} >
                                                                <option value="">Select Language</option>
                                                                {languages.map((item) => (
                                                                    <option key={item.languageId} value={item.languageId}>{item.languageName}</option>
                                                                ))}
                                                            </select>
                                                            <small className="text-danger"> {errors.languageId?.message} </small>
                                                        </div>
                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Search Keyword</label>
                                                            <input type="text" name="searchKeywords" class="form-control" {...register("searchKeywords")} />
                                                        </div>

                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Course Tag</label>
                                                            <select name="courseTag" class="form-select" placeholder='Select Course Tag' {...register("courseTag")} >
                                                                <option value="">Select Course Tag</option>
                                                                <option value="🔥 Trending">🔥 Trending</option>
                                                                <option value="⭐ Best Seller">⭐ Best Seller</option>
                                                                <option value="👑 Most Popular">👑 Most Popular</option>
                                                                <option value="🚀 Top Rated">🚀 Top Rated</option>
                                                                <option value="💯 Recommended">💯 Recommended</option>
                                                                <option value="❤️ Student Favorite">❤️ Student Favorite</option>
                                                            </select>
                                                        </div>

                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Trailer Url</label>
                                                            <input type="text" name="trailerUrl" class="form-control" {...register("trailerUrl")} />
                                                            <small className="text-danger"> {errors.trailerUrl?.message} </small>
                                                        </div>

                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Students Enrolled</label>
                                                            <input type="number" name="studentsEnrolled" class="form-control" {...register("studentsEnrolled")} />
                                                            <small className="text-danger"> {errors.studentsEnrolled?.message} </small>

                                                        </div>

                                                        <div class="col-md-3 mb-3">
                                                            <label class="form-label">Course Level</label>
                                                            <select name="courseLevel" class="form-select" {...register("courseLevel")} >
                                                                <option value="">Select Course Level</option>
                                                                <option value="Beginner">Beginner</option>
                                                                <option value="Intermediate">Intermediate</option>
                                                                <option value="Advanced">Advanced</option>
                                                            </select>
                                                            <small className="text-danger"> {errors.courseLevel?.message} </small>
                                                        </div>
                                                        {courseData?.courseImage ? (
                                                            <div class="col-md-3">
                                                                <label class="form-label">Course thumbnail</label>
                                                                <br />
                                                                <div class="mt-3">
                                                                    <div class="d-inline-block ">
                                                                        <div class="position-relative " style={{ width: "fit-content;" }}>
                                                                            <a target="_blank">
                                                                                <img src={`${process.env.REACT_APP_BASE_URL}/${courseData.courseImage}`} id="existingImagePhoto" class="img-thumbnail" />
                                                                            </a>
                                                                            <button type="button" class="btn btn-danger btn-sm position-absolute top-0 end-0" onClick={() => confirmDeleteDailog()}> &times; </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div class="col-md-3">
                                                                    <label class="form-label">Course thumbnail</label>
                                                                    <br />
                                                                    <div>
                                                                        <input type="file" accept=".jpeg, .jpg, .png, .webp" name="courseImage" {...register("courseImage")} class="form-control" />
                                                                    </div>
                                                                    <small className="text-danger">{errors.courseImage?.message}</small>
                                                                    {previewImage?.[0] && (
                                                                        <div class="mt-3">
                                                                            <div class="d-inline-block ">
                                                                                <div class="position-relative " style={{ width: "fit-content;" }}>
                                                                                    <a target="_blank">
                                                                                        <img src={URL.createObjectURL(previewImage[0])} id="existingImagePhoto" class="img-thumbnail" />
                                                                                    </a>
                                                                                    <button type="button" class="btn btn-danger btn-sm position-absolute top-0 end-0" onClick={() => resetField('courseImage')}> &times; </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                        <div class="col-md-6 mb-3">
                                                            <label class="form-label">Features</label>
                                                            <textarea rows={4} type="text" name="features" class="form-control" placeholder='Features' {...register("features")} />
                                                            <small className="text-danger">{errors.features?.message}</small>
                                                        </div>
                                                        <div class="col-md-6 mb-3">
                                                            <label class="form-label">What You will Learn</label>
                                                            <textarea rows={4} type="text" name="whatYouWillLearn" class="form-control" {...register("whatYouWillLearn")} />
                                                            <small className="text-danger">{errors.whatYouWillLearn?.message}</small>
                                                        </div>

                                                        <div class="col-md-3 mt-3">
                                                            <label class="form-label">Status</label>
                                                            <div class="form-check form-switch mb-2">
                                                                <input class="form-check-input mt-2" name="isActive" type="checkbox" id="flexSwitchCheckDefault" {...register("isActive")} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="mt-4">
                                                        <button type="submit" class="btn btn-primary me-3">Save</button>
                                                        <button type="button" onClick={() => resetValue()} class="btn btn-secondary reset-btn">Reset</button>
                                                    </div>
                                                </form>
                                            </TabPanel>
                                            <TabPanel disabled={!isEdit} header="Content">
                                                <AddContent courseId={courseData?.courseId} />
                                            </TabPanel>
                                            <TabPanel disabled={!isEdit} header="Related Courses">
                                                <AddRelatedCourses />
                                            </TabPanel>
                                            <TabPanel disabled={!isEdit} header="Cross Sell Courses">
                                                <AddCrossSell />
                                            </TabPanel>
                                        </TabView>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    )
}

export default CourseAddEdit
